import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/stores/authStore';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';

const loginSchema = z.object({
  // Логин может быть как email, так и короткой строкой (бэкенд принимает
  // свободный логин). Поэтому НЕ требуем формат email — иначе короткие
  // логины вроде «kiberbara» не проходят валидацию.
  email: z.string().min(1, 'Введите логин или email'),
  password: z.string().min(1, 'Введите пароль'),
});

type LoginFormData = z.infer<typeof loginSchema>;

/**
 * Форма входа портала. Используется и на отдельной /login, и инлайн на
 * главной (разворачивается на месте кнопки «Войти в систему»).
 */
const LoginForm: React.FC<{ onClose?: () => void; autoFocus?: boolean }> = ({ onClose, autoFocus }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading } = useAuthStore();
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      await login(data.email, data.password);
      toast.success('Вы успешно вошли в систему');
      navigate(from, { replace: true });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string }; status?: number } };
      if (err.response?.status === 401) {
        setError('root', { message: 'Неверный email или пароль' });
      } else if (err.response?.data?.message) {
        setError('root', { message: err.response.data.message });
      } else {
        setError('root', { message: 'Произошла ошибка. Попробуйте позже.' });
      }
    }
  };

  return (
    <div className="cc-glass px-4 py-5">
      <div className="text-center">
        <h1 className="cc-display text-xl font-extrabold text-fg-primary">Вход</h1>
        <p className="mt-1 text-[12.5px] text-fg-secondary">Портал технической поддержки</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-3.5">
        {errors.root && (
          <div className="rounded-xl border border-danger-border bg-danger-bg p-3.5 text-sm text-danger">
            {errors.root.message}
          </div>
        )}

        <Input
          label="Email или логин"
          type="text"
          placeholder="example@company.ru или логин"
          error={errors.email?.message}
          autoComplete="username"
          autoFocus={autoFocus}
          className="rounded-xl"
          {...register('email')}
        />

        <Input
          label="Пароль"
          type="password"
          placeholder="Введите пароль"
          error={errors.password?.message}
          autoComplete="current-password"
          className="rounded-xl"
          {...register('password')}
        />

        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={isLoading}
          className="cc-accent-grad cc-cta cc-glow w-full rounded-2xl text-accent-text shadow-[0_18px_42px_-18px_var(--accent-border)]"
        >
          Войти
        </Button>
      </form>

      <p className="mt-4 text-center text-[12px] text-fg-muted">
        Доступ выдаёт партнёр, подключивший ваш клуб к Club:Core. Логин и пароль — у него.
      </p>

      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="mt-2 block w-full text-center text-[12px] text-fg-muted transition-colors hover:text-fg-secondary"
        >
          Свернуть
        </button>
      )}
    </div>
  );
};

export default LoginForm;
